import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Trophy, Medal, Store } from "lucide-react";

export default function StreakBoard() {
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/stats/me").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/stats/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
  }, []);

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="streak-board">
      {/* Streak card */}
      <div className="rounded-3xl border border-brand-line bg-brand text-white p-6 relative overflow-hidden grain">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-white/80 text-sm"><Flame className="w-4 h-4" /> Weekly streak</div>
          <p className="font-display text-6xl mt-3 leading-none" data-testid="streak-weeks">{stats?.streak_weeks ?? 0}</p>
          <p className="text-white/80 mt-1">{(stats?.streak_weeks ?? 0) === 1 ? "week" : "weeks"} in a row</p>
          {stats?.badge && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-3 py-1 text-sm font-semibold" data-testid="streak-badge">
              <span>{stats.badge.emoji}</span> {stats.badge.name}
            </div>
          )}
          {stats?.next_badge && (
            <p className="text-white/70 text-xs mt-2" data-testid="next-badge">
              {stats.next_badge.weeks - (stats.streak_weeks ?? 0)} more week{stats.next_badge.weeks - (stats.streak_weeks ?? 0) === 1 ? "" : "s"} to {stats.next_badge.emoji} {stats.next_badge.name}
            </p>
          )}
          <div className="mt-5 pt-4 border-t border-white/20 flex items-center justify-between text-sm">
            <div>
              <p className="text-white/70">This week</p>
              <p className="font-semibold text-lg">{stats?.active_days_this_week ?? 0} day{(stats?.active_days_this_week ?? 0) === 1 ? "" : "s"}</p>
            </div>
            <div className="text-right">
              <p className="text-white/70">Total listens</p>
              <p className="font-semibold text-lg">{stats?.total_listens ?? 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top listeners */}
      <div className="rounded-3xl border border-brand-line bg-white p-6">
        <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-brand-dough" /><h4 className="font-display text-xl text-brand-ink">Top listeners</h4></div>
        <div className="space-y-2" data-testid="top-listeners">
          {(!board || board.top_users.length === 0) && <p className="text-sm text-brand-muted py-4 text-center">No listens logged yet.</p>}
          {board?.top_users.map((p, i) => (
            <div key={p.user_id} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${p.is_me ? "bg-brand/10" : ""}`}>
              <span className={`w-6 text-center font-display text-lg ${i < 3 ? "text-brand-dough" : "text-brand-muted"}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-ink truncate">{p.name}{p.is_me && " (you)"}</p>
                <p className="text-xs text-brand-muted">Store {p.store}</p>
              </div>
              <span className="text-sm font-semibold text-brand">{p.listens}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cafe leaderboard */}
      <div className="rounded-3xl border border-brand-line bg-white p-6">
        <div className="flex items-center gap-2 mb-4"><Store className="w-5 h-5 text-brand" /><h4 className="font-display text-xl text-brand-ink">Cafe leaderboard</h4></div>
        <div className="space-y-2" data-testid="cafe-leaderboard">
          {(!board || board.stores.length === 0) && <p className="text-sm text-brand-muted py-4 text-center">No cafe data yet.</p>}
          {board?.stores.map((s, i) => (
            <div key={s.store} className="flex items-center gap-3 rounded-lg px-3 py-2">
              <Medal className={`w-4 h-4 ${i === 0 ? "text-brand-dough" : "text-brand-muted"}`} />
              <span className="flex-1 text-sm font-semibold text-brand-ink">Store {s.store}</span>
              <span className="text-sm font-semibold text-brand">{s.listens} listens</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
